import { engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { HtmlContainerElement } from "../HTMLComponents/Elements/HtmlContainerElement";
import { SchemasManager } from "../Managers/SchemasManager";
import { HtmlButtonElement } from "../HTMLComponents/Elements/HtmlButtonElement";
import { RouletteActions } from "../Channels/Common/Actions/RouletteActions";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { HtmlSchemaSelectButtonElement } from "./HtmlSchemaSelectButtonElement";
import { DataManager } from "../Managers/DataManager";
import { Bet } from "../GameComponents/Bet";
import { Languages } from "../Utils/Languages";
import { HtmlToastMessage } from "../HTMLComponents/Elements/HtmlToastMessage";
import { HtmlDefaultSchemeSelectorContainerElement } from "../HTMLComponents/Custom/HtmlDefaultSchemeSelectorContainerElement";
import { BetManager } from "../Managers/BetManager";
import { Scaling } from "../Utils/Scaling";
import { HtmlTouchContainerElement } from "./HtmlTouchContainerElement";
import { IEventEmitter } from "neo-common/Engine/Utils/EventEmitterBase";
import { PlayList } from "../Components/Sounds/PlayList";

@engineComponent(ComponentTypes.HtmlElement)
export class HtmlSchemasContainerElement extends HtmlTouchContainerElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public static readonly ElementId: string = "favoriteSchemas";

    private static VisibleSlidesCount = 8;

    private static MobileVisibleSlidesCount = 1;

    private static LastElementFix = 1;

    private static MobileLastElementFix = 4;

    private schemaPlayerSelectButtons: Array<HtmlButtonElement>;

    private defaultSchemasContainer: HtmlDefaultSchemeSelectorContainerElement;

    private schemaModePlayer: boolean;

    private slide: number = 0;

    private maxSlide: number = 1;

    private slideWidth: number;

    private slideHeight: number;

    private nextPlayerButton: HTMLElement;

    private previousPlayerButton: HTMLElement;

    private playerWrapper: HTMLElement;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.schemaModePlayer = true;
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    create(node: Element): void {
        super.create(node);

        if (window["CHANNEL"] == "mobile") {
            HtmlSchemasContainerElement.VisibleSlidesCount = HtmlSchemasContainerElement.MobileVisibleSlidesCount;
            HtmlSchemasContainerElement.LastElementFix = HtmlSchemasContainerElement.MobileLastElementFix;
            this.maxSlide = SchemasManager.getPlayerSchemasCount() / HtmlSchemasContainerElement.VisibleSlidesCount;
        }

        this.createPlayerSchemaButtons();
        this.createPlayerFakeControls();
        this.createPlayerControls();
        this.createPlayerSwipeControls();

        if (Scaling.Emitter != null) {
            Scaling.Emitter.on(Scaling.EventResize, this.updateSlider.bind(this, false));
        }

        this.defaultSchemasContainer = <HtmlDefaultSchemeSelectorContainerElement>(
            HtmlController.ContainerElementsById["favoriteDefaultSchemasHolder"]
        );
    }

    private createPlayerSchemaButtons(): void {
        let schemaDelete = document.getElementById("schemaDelete");
        let slideHolder = document.getElementById("favoritePlayerSchemasWrapper");
        this.playerWrapper = slideHolder != null ? slideHolder : document.getElementById("favoritePlayerSchemasHolder");

        if (this.playerWrapper) {
            this.schemaPlayerSelectButtons = [];
            for (let i = 0; i < SchemasManager.getPlayerSchemasCount(); i++) {
                let button = this.createPlayerSchemaButton(i, RouletteActions.ActionSchemasPlayerSelect, "+");
                this.schemaPlayerSelectButtons.push(button);
                this.playerWrapper.insertBefore(this.schemaPlayerSelectButtons[i].instance, schemaDelete);
            }
        }

        this.updatePlayerSchemeButtons();
    }

    private createPlayerSchemaButton(index: number, action: string, text: string): HtmlButtonElement {
        let button = new HtmlSchemaSelectButtonElement(index);
        button.action = action;
        button.registerEvents();
        button.instance.classList.add("schemaBtn");
        button.setText(text);
        HtmlController.registerActionElement(action, button);

        return button;
    }

    private createPlayerControls(): void {
        if (this.maxSlide == 1) {
            return;
        }

        const holder = document.getElementById("favoriteSchemasControls");
        if (holder != null && this.maxSlide) {
            this.nextPlayerButton = this.createControlButton(holder, "schemaBtnNext", this.nextSlide.bind(this));
            this.previousPlayerButton = this.createControlButton(
                holder,
                "schemaBtnPrevious",
                this.previousSlide.bind(this),
            );
        }
    }

    private createPlayerSwipeControls(): void {
        if (window["CHANNEL"] == "mobile") {
            //this.on(HtmlSchemasContainerElement.evMoveLeft, this.swipeNextSlide.bind(this, true));
            this.on(HtmlSchemasContainerElement.evMoveUp, this.swipeNextSlide.bind(this));

            //this.on(HtmlSchemasContainerElement.evMoveRight, this.swipePreviousSlide.bind(this, true));
            this.on(HtmlSchemasContainerElement.evMoveDown, this.swipePreviousSlide.bind(this));

            let schemaDelete = document.getElementById("schemaDelete") as HTMLElement;
            schemaDelete.addEventListener("pointerdown", () => {
                this.deleteSchema();
            });
        }
    }

    private createControlButton(holder: HTMLElement, className: string, action: () => void): HTMLElement {
        let button: HTMLElement = document.createElement("div");
        button.classList.add("btn", "schemaControlBtn", "schemaPlayerControlBtn", className);
        if (className == "schemaBtnPrevious") button.classList.add("_disabled");
        button.onclick = action;
        holder.appendChild(button);
        return button;
    }

    private createPlayerFakeControls(): void {
        const holder = document.getElementById("favoriteSchemasFakeControls");

        if (holder != null) {
            const button = this.createPlayerSchemaButton(-1, RouletteActions.ActionSchemasPlayerSelect, "");
            holder.appendChild(button.instance);
        }
    }

    //******************************************************************************************************************
    //  PUBLIC HANDLERS
    //******************************************************************************************************************

    public toggleEnable(): void {
        this.setEnable(this.instance.classList.contains("_disabled"));
    }

    public setEnable(enable: boolean): void {
        if (enable) {
            this.instance.classList.remove("_disabled");
            document.body.classList.add("schemas-on");
        } else {
            this.instance.classList.add("_disabled");
            document.body.classList.remove("schemas-on");
            this.resetSchemas();
        }
    }

    public selectScheme(bets: Array<Bet>, playerSchema: boolean): Array<Bet> {
        let currentBets: Array<Bet>;
        if (playerSchema) {
            currentBets = this.selectPlayerScheme(bets);
        } else {
            currentBets = this.defaultSchemasContainer.selectDefaultScheme(bets);
        }

        currentBets = this.mergeBets(currentBets, bets);
        return currentBets;
    }

    public getSelectedSchemeBets(playerSchema: boolean): Array<Bet> {
        if (playerSchema) {
            return SchemasManager.getPlayerSchemaByIndex(DataManager.SchemaSelected);
        } else {
            return SchemasManager.getDefaultSchemaByIndex(DataManager.SchemaSelected);
        }
    }

    public deleteSchema(): void {
        SchemasManager.removeSchema(DataManager.SchemaSelected);
        DataManager.SchemaSelected = -1;

        this.resetSchemas();
        this.updatePlayerSchemeButtons();
    }

    public saveSchema(bets: Array<Bet>): void {
        this.resetSchemas();

        if (bets.length == 0) {
            const placeHolderId = window["CHANNEL"] == "mobile" ? "toastMessageHolder" : "singleGridBetPlaces";
            HtmlToastMessage.show(Languages.getRid("ERROR_PLACE_BET"), HtmlToastMessage.Position.middle, placeHolderId);
            return;
        }

        let schemaBets: Array<Bet> = [];
        for (let i = 0; i < bets.length; i++) {
            schemaBets.push(new Bet(bets[i].type, bets[i].selection, bets[i].value));
        }

        SchemasManager.addSchema(DataManager.SchemaSelected, schemaBets);

        this.updatePlayerSchemeButtons();
    }

    public switchSchemas(): void {
        const defaultButtonsHolder = document.getElementById("favoriteDefaultSchemasHolder");
        const playerButtonsHolder = document.getElementById("favoritePlayerSchemasHolder");
        const switchBtn = document.getElementById("schemaSwitch");

        if (defaultButtonsHolder != null && playerButtonsHolder != null) {
            if (defaultButtonsHolder.classList.contains("_disabled")) {
                defaultButtonsHolder.classList.remove("_disabled");
                playerButtonsHolder.classList.add("_disabled");
                switchBtn.classList.add("_active");
                this.schemaModePlayer = false;
            } else {
                defaultButtonsHolder.classList.add("_disabled");
                playerButtonsHolder.classList.remove("_disabled");
                switchBtn.classList.remove("_active");
                this.schemaModePlayer = true;
            }
        }

        this.defaultSchemasContainer.showPagination(!this.schemaModePlayer);
        this.resetSchemas();
    }

    public isPlayerMode(): boolean {
        return this.schemaModePlayer;
    }

    //******************************************************************************************************************
    //  PRIVATE FUNCTIONS
    //******************************************************************************************************************

    private selectPlayerScheme(bets: Array<Bet>): Array<Bet> {
        let currentBets = SchemasManager.getPlayerSchemaByIndex(DataManager.SchemaSelected);
        if (currentBets.length == 0 && DataManager.SchemaSelected > -1) {
            this.saveSchema(bets);
            return [];
        }

        this.resetSchemas();
        let button = this.schemaPlayerSelectButtons[DataManager.SchemaSelected];
        if (button != null) {
            button.instance.classList.add("_active");
        }

        return currentBets;
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    private resetSchemas(): void {
        for (let i = 0; i < this.schemaPlayerSelectButtons.length; i++) {
            const button = this.schemaPlayerSelectButtons[i];
            button.instance.classList.remove("_active");
        }

        this.defaultSchemasContainer.resetSchemas(true);
    }

    private updatePlayerSchemeButtons(): void {
        for (let i = 0; i < this.schemaPlayerSelectButtons.length; i++) {
            const button = this.schemaPlayerSelectButtons[i];
            let text = "+";

            if (SchemasManager.getPlayerSchemaByIndex(i).length > 0) {
                text = (i + 1).toString();
            }

            if (button != null) {
                button.setText(text);
            }
        }
    }

    private mergeBets(bets: Array<Bet>, tableBets: Array<Bet>): Array<Bet> {
        const result: Array<Bet> = [];
        for (let i = 0; i < bets.length; i++) {
            let currentIndex = -1;

            result.some((value, index) => {
                if (value.selection == bets[i].selection && value.type == bets[i].type) {
                    currentIndex = index;
                }
                return false;
            });

            const betValue = bets[i].value == -1 ? BetManager.Chip : bets[i].value;
            if (currentIndex > -1) {
                result[currentIndex].value = parseFloat(NumberUtils.add(result[currentIndex].value, betValue));
                continue;
            } else {
                result.push(new Bet(bets[i].type, bets[i].selection, betValue));
                currentIndex = result.length - 1;
            }

            for (let j = 0; j < tableBets.length; j++) {
                if (bets[i].type == tableBets[j].type && bets[i].selection == tableBets[j].selection) {
                    result[currentIndex].value = parseFloat(
                        NumberUtils.add(result[currentIndex].value, tableBets[j].value),
                    );
                }
            }
        }

        return result;
    }

    private swipeNextSlide(): void {
        PlayList.play("SND_Click");
        this.nextSlide();
    }

    private swipePreviousSlide(): void {
        PlayList.play("SND_Click");
        this.previousSlide();
    }

    private nextSlide(): void {
        if (this.slide == this.maxSlide - HtmlSchemasContainerElement.LastElementFix) {
            return;
        }

        this.slide++;
        this.updateSlider(true);
        this.updatePlayerControls();
    }

    private previousSlide(): void {
        if (this.slide == 0) {
            return;
        }

        this.slide--;
        this.updateSlider(true);
        this.updatePlayerControls();
    }

    private updatePlayerControls(): void {
        if (this.nextPlayerButton == null || this.previousPlayerButton == null) {
            return;
        }

        this.nextPlayerButton.classList.remove("_disabled");
        this.previousPlayerButton.classList.remove("_disabled");

        if (this.slide == this.maxSlide - HtmlSchemasContainerElement.LastElementFix) {
            this.nextPlayerButton.classList.add("_disabled");
        }

        if (this.slide == 0) {
            this.previousPlayerButton.classList.add("_disabled");
        }
    }

    private updateSlider(animate: boolean = false): void {
        this.slideWidth =
            parseInt(window.getComputedStyle(document.getElementById("favoritePlayerSchemasHolder")).width) / 4;
        this.slideHeight =
            parseInt(window.getComputedStyle(document.getElementById("favoritePlayerSchemasHolder")).height) / 4;

        this.playerWrapper.style.transition = `transform ${animate ? 200 : 0}ms`;
        this.playerWrapper.offsetWidth;

        this.playerWrapper.style.transform = `translate3d(0,${this.slide * this.slideHeight * -1 - 5 * this.slide}px,0)`;
        this.playerWrapper.style.width = "";
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/HtmlSchemasContainerElement.ts
 **/